/**
 * Un panneau étroit collé à droite de l'écran, PAR-DESSUS le cockpit et sans
 * voile : ce qu'on règle dedans se voit tout de suite sur le vrai cockpit, à
 * côté. Le patron est celui de Discord (« Preview Theme ») — Kayla,
 * 07-10-2026 : « je voulais pas de modal. Regarde Discord […] c'est juste un
 * petit truc à droite ».
 *
 * Porté dans <body> : aucun panneau du shell ne le rogne. Sa hauteur suit
 * `--zoom-affichage`, comme le shell (cf App.tsx), sinon il déborde quand la
 * taille d'affichage n'est pas 100 %.
 *
 * Dessin (coque, 01-11-2026) : la fenêtre en verre de la librairie (celle de
 * `Modal` / `Drawer`, `ov-fenetre`) — flottante à 12 px des bords, grand
 * arrondi, SANS le voile qui les accompagne —, l'en-tête de `Modal`
 * (`ModalHeader` : le titre en Almarena, la croix de la librairie).
 */
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { ModalHeader } from "@/shared/ui";

export function PanneauLateral({
  titre,
  fermer,
  pied,
  children,
}: {
  titre: ReactNode;
  /** Absent = le panneau ne se ferme pas (l'accueil se ferme par son bouton). */
  fermer?: () => void;
  pied?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!fermer) return;
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === "Escape") fermer();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [fermer]);

  return createPortal(
    <aside
      className="ov-fenetre ov-arrondi fixed right-3 top-3 z-[450] h-[calc(100dvh/var(--zoom-affichage,1)-24px)] w-[340px] max-w-[calc(100vw/var(--zoom-affichage,1)-24px)] text-fg"
      aria-label={typeof titre === "string" ? titre : undefined}
    >
      {typeof titre === "string" ? (
        <ModalHeader title={titre} onClose={fermer} closeLabel={t("common.close")} />
      ) : (
        <ModalHeader onClose={fermer} closeLabel={t("common.close")}>
          {titre}
        </ModalHeader>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-4">{children}</div>
      {pied && <div className="shrink-0 border-t border-fg/10 px-5 py-4">{pied}</div>}
    </aside>,
    document.body,
  );
}
